// Employee self-service intake form. One-time-use, opened via the URL HR
// emailed:  /#intake?token=<64-hex>
//
// Standalone (no sidebar / no auth) — mirrors PublicApply. Layered:
// employee fills → submits → status flips to Submitted → token becomes
// dead → HR reviews in Employees → Intake Review before merging into the
// live employee record.
//
// Every field is mandatory — the employee can't submit until the whole
// form (including every document upload) is filled in.

import React, { useState, useEffect } from "react";
import { Upload, Send, CheckCircle, AlertTriangle } from "lucide-react";
import { BASE } from "../api";

const input = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";
const label = "block text-[14px] font-semibold text-slate-600 uppercase tracking-wide mb-1";
const sec = "text-sm font-bold text-slate-700 uppercase tracking-wide mb-3";

const DOC_FIELDS = [
  { key: 'passport_photo',    l: 'Passport-Size Photo' },
  { key: 'aadhaar_card',      l: 'Aadhaar Card' },
  { key: 'pan_card',          l: 'PAN Card' },
  { key: 'tenth_marksheet',   l: '10th Marksheet' },
  { key: 'twelfth_marksheet', l: '12th Marksheet' },
  { key: 'highest_degree',    l: 'Highest Degree' },
  { key: 'uan_screenshot',    l: 'UAN Screenshot' },
];

export default function PublicIntake() {
  // Support both ?token=… on the path AND #intake?token=… on the hash
  // so the link works whether the app is served under a rewriting proxy
  // (path routing) or straight from the static build (hash routing).
  const rawQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : window.location.search;
  const token = new URLSearchParams(rawQuery).get('token') || '';

  const [state, setState] = useState('loading'); // loading | ready | expired | submitted | error | done
  const [errorMsg, setErrorMsg] = useState('');
  // Network/server hiccup (worth a Retry button) vs. a definitive answer
  // like "expired" or "invalid token" (retrying won't help — same result).
  const [canRetry, setCanRetry] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [brand, setBrand] = useState({ company_name: '', logo_url: null });
  const [form, setForm] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('Missing invite token in the URL. Please re-open the link HR sent you.'); return; }
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/intake/${token}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setErrorMsg(data.error || 'This link is not valid.');
          setCanRetry(res.status === 429 || res.status >= 500);
          setState(res.status === 410 ? 'expired' : 'error');
          return;
        }
        setBrand(data.brand || {});
        setForm({
          salutation: '',
          first_name: data.prefill.first_name || (data.prefill.name || '').split(' ')[0] || '',
          last_name:  data.prefill.last_name  || (data.prefill.name || '').split(' ').slice(1).join(' ') || '',
          father_name: '',
          dob: data.prefill.dob || '',
          gender: data.prefill.gender || '',
          marital_status: '',
          personal_email: data.prefill.personal_email || '',
          phone: data.prefill.phone || '',
          address_current: data.prefill.address_current || '',
          address_permanent: data.prefill.address_permanent || '',
          pan_number: '',
          aadhar_number: '',
          uan_number: '',
          highest_qualification: '',
          degree: '',
          university: '',
          passing_year: '',
          percentage_cgpa: '',
          emergency_name: '',
          emergency_relation: '',
          emergency_phone: '',
        });
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        // fetch() throws a bare "Failed to fetch" TypeError on any network
        // failure (server unreachable, connection reset, DNS hiccup) — not
        // useful shown verbatim to a non-technical employee. Retrying is
        // the right call here since nothing about the link itself is wrong.
        setErrorMsg('Could not reach the server. Check your connection and try again.');
        setCanRetry(true);
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, retryTick]);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pick = (k, file) => setFiles(p => ({ ...p, [k]: file }));

  const submit = async (e) => {
    e.preventDefault();
    // Text/select/date fields are enforced by the `required` attribute (the
    // browser blocks submit before this handler runs). Server re-validates
    // everything including PAN/Aadhaar/UAN formats. File inputs are hidden
    // (styled via a label wrapper), so HTML5 `required` can't validate them
    // — browsers exclude display:none elements from constraint validation —
    // so check those here instead.
    setSubmitErr('');
    const missingDocs = DOC_FIELDS.filter(d => !files[d.key]);
    if (missingDocs.length) { setSubmitErr(`Please upload: ${missingDocs.map(d => d.l).join(', ')}`); return; }

    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
    Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });

    try {
      const res = await fetch(`${BASE}/api/intake/${token}`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setState('done');
      setErrorMsg(data.message);
    } catch (e) {
      // A network-level failure (server unreachable, connection dropped
      // mid-upload) throws a bare "Failed to fetch" — not useful shown
      // verbatim. Everything typed/attached is still in state, so the
      // employee can just retry the submit rather than starting over.
      setSubmitErr(e instanceof TypeError
        ? "Couldn't reach the server — check your connection and try submitting again. Nothing you've entered was lost."
        : (e.message || "Submission failed. Please try again."));
    }
    finally { setSubmitting(false); }
  };

  if (state === 'loading') {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  }
  if (state === 'error' || state === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"><AlertTriangle size={28} className="text-amber-600" /></div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">{state === 'expired' ? 'Link no longer active' : 'Something went wrong'}</h1>
          <p className="text-sm text-slate-600">{errorMsg || 'Please reach out to HR.'}</p>
          {canRetry && <button onClick={() => setRetryTick(t => t + 1)} className="mt-4 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">Try Again</button>}
        </div>
      </div>
    );
  }
  if (state === 'done') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle size={28} className="text-emerald-600" /></div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">All done</h1>
          <p className="text-sm text-slate-600 leading-relaxed">{errorMsg || 'Thanks — your details are with HR.'}</p>
        </div>
      </div>
    );
  }

  const FileRow = ({ k, l }) => (
    <label className="flex items-center gap-2 p-3 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 cursor-pointer text-slate-600 text-xs hover:border-emerald-400 hover:bg-emerald-50">
      <Upload size={13} className="text-emerald-600 flex-shrink-0" />
      <span className="flex-1 truncate"><b className="text-slate-900">{l}</b> — {files[k]?.name || 'click to upload'}</span>
      <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={e => pick(k, e.target.files[0])} />
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          {brand.logo_url && <img src={`${BASE}${brand.logo_url}`} alt="" className="h-14 mx-auto mb-3" />}
          <h1 className="text-2xl font-extrabold text-slate-900">Welcome to {brand.company_name || 'the team'}</h1>
          <p className="text-sm text-slate-600 mt-1">Please fill in a few details. HR will review before it goes on your record.</p>
          <p className="text-[14px] text-slate-500 mt-1 italic">This link is single-use — you can only submit once.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-8">
          <div>
            <h2 className={sec}>Basic Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={label}>Salutation <span className="text-red-500">*</span></label>
                <select className={input} value={form.salutation} onChange={e => upd('salutation', e.target.value)} required>
                  <option value="" disabled>—</option>{['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.'].map(s => <option key={s}>{s}</option>)}
                </select></div>
              <div className="sm:col-span-1" />
              <div><label className={label}>First Name <span className="text-red-500">*</span></label>
                <input className={input} value={form.first_name} onChange={e => upd('first_name', e.target.value)} required /></div>
              <div><label className={label}>Last Name <span className="text-red-500">*</span></label>
                <input className={input} value={form.last_name} onChange={e => upd('last_name', e.target.value)} required /></div>
              <div><label className={label}>Date of Birth <span className="text-red-500">*</span></label>
                <input type="date" className={input} value={form.dob} onChange={e => upd('dob', e.target.value)} required /></div>
              <div><label className={label}>Gender <span className="text-red-500">*</span></label>
                <select className={input} value={form.gender} onChange={e => upd('gender', e.target.value)} required>
                  <option value="" disabled>—</option><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
                </select></div>
              <div><label className={label}>Marital Status <span className="text-red-500">*</span></label>
                <select className={input} value={form.marital_status} onChange={e => upd('marital_status', e.target.value)} required>
                  <option value="" disabled>—</option>{['Single', 'Married', 'Divorced', 'Widowed'].map(s => <option key={s}>{s}</option>)}
                </select></div>
              <div><label className={label}>Father's Name <span className="text-red-500">*</span></label>
                <input className={input} value={form.father_name} onChange={e => upd('father_name', e.target.value)} required /></div>
            </div>
          </div>

          <div>
            <h2 className={sec}>Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={label}>Personal Email <span className="text-red-500">*</span></label>
                <input type="email" className={input} value={form.personal_email} onChange={e => upd('personal_email', e.target.value)} required /></div>
              <div><label className={label}>Mobile <span className="text-red-500">*</span></label>
                <input type="tel" className={input} value={form.phone} onChange={e => upd('phone', e.target.value.replace(/[^\d+\-() ]/g, ''))} required /></div>
              <div className="sm:col-span-2"><label className={label}>Present Address <span className="text-red-500">*</span></label>
                <textarea rows={2} className={input} value={form.address_current} onChange={e => upd('address_current', e.target.value)} required /></div>
              <div className="sm:col-span-2"><label className={label}>Permanent Address <span className="text-red-500">*</span></label>
                <textarea rows={2} className={input} value={form.address_permanent} onChange={e => upd('address_permanent', e.target.value)} required /></div>
            </div>
          </div>

          <div>
            <h2 className={sec}>Statutory IDs</h2>
            <p className="text-[14px] text-slate-500 mb-3 -mt-2">PAN, Aadhaar and UAN are all required for payroll compliance.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className={label}>PAN <span className="text-red-500">*</span></label>
                <input className={input} value={form.pan_number} onChange={e => upd('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} required /></div>
              <div><label className={label}>Aadhaar (12 digits) <span className="text-red-500">*</span></label>
                <input className={input} value={form.aadhar_number} onChange={e => upd('aadhar_number', e.target.value.replace(/\D/g, ''))} maxLength={12} required /></div>
              <div><label className={label}>UAN (12 digits) <span className="text-red-500">*</span></label>
                <input className={input} value={form.uan_number} onChange={e => upd('uan_number', e.target.value.replace(/\D/g, ''))} maxLength={12} required /></div>
            </div>
          </div>

          <div>
            <h2 className={sec}>Education (highest)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={label}>Highest Qualification <span className="text-red-500">*</span></label>
                <input className={input} value={form.highest_qualification} onChange={e => upd('highest_qualification', e.target.value)} placeholder="B.TECH" required /></div>
              <div><label className={label}>Degree / Specialisation <span className="text-red-500">*</span></label>
                <input className={input} value={form.degree} onChange={e => upd('degree', e.target.value)} required /></div>
              <div><label className={label}>University / College <span className="text-red-500">*</span></label>
                <input className={input} value={form.university} onChange={e => upd('university', e.target.value)} required /></div>
              <div><label className={label}>Passing Year <span className="text-red-500">*</span></label>
                <input type="number" min="1970" max="2100" className={input} value={form.passing_year} onChange={e => upd('passing_year', e.target.value)} required /></div>
              <div><label className={label}>Percentage / CGPA <span className="text-red-500">*</span></label>
                <input className={input} value={form.percentage_cgpa} onChange={e => upd('percentage_cgpa', e.target.value)} placeholder="8.4 / 84%" required /></div>
            </div>
          </div>

          <div>
            <h2 className={sec}>Emergency Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className={label}>Name <span className="text-red-500">*</span></label>
                <input className={input} value={form.emergency_name} onChange={e => upd('emergency_name', e.target.value)} required /></div>
              <div><label className={label}>Relation <span className="text-red-500">*</span></label>
                <select className={input} value={form.emergency_relation} onChange={e => upd('emergency_relation', e.target.value)} required>
                  <option value="" disabled>—</option>{['Father', 'Mother', 'Spouse', 'Other'].map(s => <option key={s}>{s}</option>)}
                </select></div>
              <div><label className={label}>Phone <span className="text-red-500">*</span></label>
                <input type="tel" className={input} value={form.emergency_phone} onChange={e => upd('emergency_phone', e.target.value.replace(/[^\d+\-() ]/g, ''))} required /></div>
            </div>
          </div>

          <div>
            <h2 className={sec}>Documents</h2>
            <p className="text-[14px] text-slate-500 mb-3 -mt-2">PDF / DOC / DOCX / JPG / PNG, up to 5 MB each — all required. HR sees these on review.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DOC_FIELDS.map(d => <FileRow key={d.key} k={d.key} l={d.l} />)}
            </div>
          </div>

          {submitErr && <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{submitErr}</div>}

          <button type="submit" disabled={submitting}
            className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={16} />{submitting ? 'Submitting…' : 'Submit for HR Review'}
          </button>
          <p className="text-[14px] text-slate-500 text-center">
            You'll only see this form once. If you need to change something later, contact HR.
          </p>
        </form>
      </div>
    </div>
  );
}
